import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  CreditCard,
  MapPin,
  User,
  Mail,
  Phone,
  Lock,
  CheckCircle,
  ArrowLeft,
  Plus,
  Check
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import apiCheckService from '../services/apiCheck';
import { useAsyncOperation } from '../hooks/useApi';
import { useUser } from '../hooks/useUser';
import { useApi } from '../hooks/useApi';

const Checkout = () => {
  const navigate = useNavigate();
  const { items, total, clearCart } = useCart();
  const { loading, error, execute } = useAsyncOperation();
  const { user } = useUser();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    direccion: '',
    ciudad: '',
    zip: '',
    country: 'España',
    telefono: '',
  });

  const [formaPago, setFormaPago] = useState('Efectivo');
  const [transaccion, setTransaccion] = useState('');
  const [bancos, setBancos] = useState([]);
  const [banco, setBanco] = useState("");

  const [address, setAddress] = useState({});
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState(null);

  const { data: addresses, loading: addressesLoading, refetch: refetchAddresses } = useApi(
    () => apiCheckService.directionsClient(user.id),
    [user.id]
  );
  /*
  useEffect(() => {
    // Si hay direcciones, seleccionar la primera por defecto
    if (addresses && addresses.length > 0 && !selectedAddressId) {
      setSelectedAddressId(addresses[0].id);
      setAddress(addresses[0]);
    }
  }, [addresses, selectedAddressId]);*/

  useEffect(() => {
    apiCheckService.bancos()
      .then(data => setBancos(data))
      .catch(err => console.error('Error:', err));
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddressSelect = (address) => {
    setSelectedAddressId(address.id);
    setAddress(address);
    setShowAddressForm(false);
  };

  const handleCreateAddress = async () => {
    try {
      const newAddress = {
        direccion: formData.direccion,
        ciudad: formData.ciudad,
        zip: formData.zip,
        pais: formData.country,
        telefono: formData.telefono,
        clienteId: user.id
      };

      const createdAddress = await execute(() => apiCheckService.createAddress(newAddress));

      // Actualizar la lista de direcciones
      await refetchAddresses();

      // Seleccionar la nueva dirección
      setSelectedAddressId(createdAddress.id);
      setAddress(createdAddress);
      setShowAddressForm(false);

      // Limpiar el formulario
      setFormData(prev => ({
        ...prev,
        direccion: '',
        ciudad: '',
        zip: '',
        telefono: ''
      }));
    } catch (error) {
      console.error('Error creating address:', error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  const validateStep = (stepNumber) => {
    switch (stepNumber) {
      case 1:
        return user.nombres && user.apellidos && user.email;
      case 2:
        return selectedAddressId
      case 3:
        if (formaPago === 'Efectivo' || formaPago === 'Pago al recibir') {
          return formaPago; 
        } else if (formaPago === 'Pago Movil' || formaPago === 'Transferencia Bancaria') {
          return formaPago && banco && transaccion; 
        }
        return false; 
      default:
        return false;
    }
  };

  const handleNextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleSubmitOrder = async () => {


    const orderData = {
      detalles: items.map(item => ({
        productoId: item.productoId,
        variedadId: item.variedadId,
        cantidad: item.cantidad,
        precio_unidad: item.precio
      })),
      total,
      forma_pago: formaPago,
      transaccion: transaccion,
      subtotal: total,
      envio: 0,
      total: total,
      clienteId: user.id,
      bancoId: banco,
      direccionId: selectedAddressId,
    };

    
    try {

      const result = await execute(() => apiCheckService.createOrder(orderData));
      setOrderId(result.id);
      setOrderComplete(true);
      clearCart();
    } catch (error) {
      console.error('Error creating order:', error);
    }
  };

  if (items.length === 0 && !orderComplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Tu carrito está vacío</h2>
          <p className="text-gray-600 mb-6">Agrega algunos productos antes de proceder al checkout</p>
          <button
            onClick={() => navigate('/products')}
            className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors"
          >
            Explorar Productos
          </button>
        </div>
      </div>
    );
  }

  if (orderComplete) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 text-center shadow-xl"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            ¡Pedido Confirmado!
          </h2>

          <p className="text-gray-600 mb-6">
            Tu pedido #{orderId} ha sido procesado exitosamente.
            Recibirás un email de confirmación en breve.
          </p>

          <div className="space-y-3">
            <button
              onClick={() => navigate('/orders')}
              className="w-full bg-blue-600 text-white py-3 rounded-xl hover:bg-blue-700 transition-colors"
            >
              Ver Mis Pedidos
            </button>

            <button
              onClick={() => navigate('/products')}
              className="w-full border border-gray-200 text-gray-900 py-3 rounded-xl hover:border-gray-300 transition-colors"
            >
              Continuar Comprando
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto p-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </button>

          <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>

          {/* Progress Steps */}
          <div className="flex items-center gap-4 mt-6">
            {[1, 2, 3].map((stepNumber) => (
              <div key={stepNumber} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= stepNumber
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-600'
                  }`}>
                  {stepNumber}
                </div>
                {stepNumber < 3 && (
                  <div className={`w-12 h-1 mx-2 ${step > stepNumber ? 'bg-blue-600' : 'bg-gray-200'
                    }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Formulario */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              {/* Paso 1: Información Personal */}
              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-6"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <User className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-bold text-gray-900">Información Personal</h2>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre
                      </label>
                      <input
                        type="text"
                        disabled
                        name="firstName"
                        value={user.nombres}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Apellidos
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        disabled
                        value={user.apellidos}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="email"
                        name="email"
                        disabled
                        value={user.email}
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>

                </motion.div>
              )}

              {/* Paso 2: Dirección de Envío */}
              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-6"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <MapPin className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-bold text-gray-900">Dirección de Envío</h2>
                  </div>

                  {addressesLoading ? (
                    <p>Cargando direcciones...</p>
                  ) : (
                    <>
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Seleccione una Dirección</h3>

                      <div className="space-y-3 mb-6">
                        {addresses && addresses.length > 0 ? (
                          addresses.map((item) => (
                            <div
                              key={item.id}
                              className={`p-4 border rounded-xl cursor-pointer transition-colors ${selectedAddressId === item.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
                              onClick={() => handleAddressSelect(item)}
                            >
                              <div className="flex justify-between items-center">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    {selectedAddressId === item.id && (
                                      <Check className="w-4 h-4 text-blue-600" />
                                    )}
                                    <p className="font-medium text-gray-900 truncate">{item.direccion}</p>
                                  </div>
                                  <p className="text-sm text-gray-500 mt-1">
                                    {item.ciudad}, {item.pais} | Tel: {item.telefono}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-500">No tienes direcciones guardadas.</p>
                        )}
                      </div>

                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900">Agregar Nueva Dirección</h3>
                        <button
                          onClick={() => setShowAddressForm(!showAddressForm)}
                          className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                        >
                          <Plus className="w-4 h-4" />
                          {showAddressForm ? 'Cancelar' : 'Nueva Dirección'}
                        </button>
                      </div>

                      {showAddressForm && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="space-y-4 p-4 border border-gray-200 rounded-xl"
                        >
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Dirección
                            </label>
                            <input
                              type="text"
                              name="direccion"
                              value={formData.direccion}
                              onChange={handleInputChange}
                              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                País
                              </label>
                              <select
                                name="country"
                                value={formData.country}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="España">España</option>
                                <option value="Francia">Francia</option>
                                <option value="Portugal">Portugal</option>
                                <option value="Italia">Italia</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Ciudad
                              </label>
                              <input
                                type="text"
                                name="ciudad"
                                value={formData.ciudad}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Código Postal
                              </label>
                              <input
                                type="text"
                                name="zip"
                                value={formData.zip}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Teléfono
                              </label>
                              <input
                                type="text"
                                name="telefono"
                                value={formData.telefono}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                                required
                              />
                            </div>
                          </div>

                          <button
                            onClick={handleCreateAddress}
                            disabled={!formData.direccion || !formData.ciudad || !formData.zip || !formData.telefono}
                            className={`px-4 py-2 rounded-xl font-medium transition-colors ${!formData.direccion || !formData.ciudad || !formData.zip || !formData.telefono
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                          >
                            Guardar Dirección
                          </button>
                        </motion.div>
                      )}
                    </>
                  )}
                </motion.div>
              )}

              {/* Paso 3: Información de Pago */}
              {step === 3 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-6"
                >
                  <div className="flex items-center gap-3 mb-6">
                    <CreditCard className="w-6 h-6 text-blue-600" />
                    <h2 className="text-xl font-bold text-gray-900">Información de Pago</h2>
                  </div>


                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Forma de Pago
                      </label>
                      <select
                        name="forma_pago"
                        value={formaPago}
                        onChange={(e) => setFormaPago(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="Efectivo">Efectivo</option>
                        <option value="Pago Movil">Pago Movil</option>
                        <option value="Transferencia Bancaria">Transferencia Bancaria</option>
                        <option value="Pago al recibir">Pago al recibir</option>
                      </select>
                    </div>

                  </div>

                  {(formaPago === 'Pago Movil' || formaPago === 'Transferencia Bancaria') && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Banco
                        </label>
                        <select
                          value={banco}
                          onChange={(e) => setBanco(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Seleccione un banco</option>
                          {bancos.map((bancoItem) => (
                            <option key={bancoItem.id} value={bancoItem.id}>
                              {bancoItem.nombre}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Referencia
                        </label>
                        <input
                          type="text"
                          name="transaccion"
                          value={transaccion}
                          onChange={(e) => setTransaccion(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                    </div>
                  )}
                </motion.div>
              )}

              {/* Botones de navegación */}
              <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
                {step > 1 && (
                  <button
                    onClick={() => setStep(step - 1)}
                    className="px-6 py-3 border border-gray-200 text-gray-900 rounded-xl hover:border-gray-300 transition-colors"
                  >
                    Anterior
                  </button>
                )}

                {step < 3 ? (
                  <button
                    onClick={handleNextStep}
                    disabled={!validateStep(step)}
                    className={`px-6 py-3 rounded-xl font-semibold transition-colors ml-auto ${validateStep(step)
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                  >
                    Siguiente
                  </button>
                ) : (
                  <button
                    onClick={handleSubmitOrder}
                    disabled={loading || !validateStep(step)}
                    className={`px-6 py-3 rounded-xl font-semibold transition-colors ml-auto ${validateStep(step) && !loading
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                  >
                    {loading ? 'Procesando...' : 'Confirmar Pedido'}
                  </button>
                )}
              </div>

              {error && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              )}
            </div>
          </div>

          {/* Resumen del pedido */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Resumen del Pedido</h3>

              <div className="space-y-3 mb-6">
                {items.map((item) => (
                  <div key={item.id} className="flex justify-between items-center">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 truncate">{item.productos.titulo}</p>
                      <p className="text-sm text-gray-500">
                        Cantidad: {item.cantidad} Precio: {formatPrice(item.precio)}
                      </p>
                    </div>
                    <p className="font-semibold text-gray-900">
                      {formatPrice(item.precio * item.cantidad)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 pt-4 space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal:</span>
                  <span>{formatPrice(total)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Envío:</span>
                  <span className="text-green-600">Gratis</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2">
                  <span>Total:</span>
                  <span className="text-blue-600">{formatPrice(total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;